// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Click-to-define glossary. `.glossary-link[data-term]` spans anywhere in the UI
 * pop a small definition tooltip; Escape or an outside click dismisses it.
 */

export const GLOSSARY: Record<string, string> = {
  'end-to-end encrypted':
    'Each file is scrambled on the sending device and only unscrambled on the receiving device. Nothing in between — no relay, no server — can read it.',
  'data channel':
    'A direct WebRTC pipe between the two browsers. Files travel over it device-to-device, encrypted on the wire, with no server in the path.',
  webrtc:
    'The browser technology for real-time connections directly between two devices — the same tech behind in-browser calling, here used to move files.',
  dtls:
    'The encryption WebRTC mandates for every peer-to-peer channel. Your files are encrypted on the wire between the two browsers.',
  'aes-gcm':
    'A strong authenticated cipher. Syncwell wraps every file’s bytes in AES-GCM-256 using a key derived from your passphrase, on top of the channel’s own encryption.',
  'file system access':
    'A browser API (Chromium) that, with your permission, lets a web page read and write a folder you pick — so Syncwell can mirror a real folder on disk, entirely locally.',
  manifest:
    'A compact list of what’s in a folder — each file’s path, size and modified time. The two devices compare manifests to work out what to send. File contents are never in it.',
  'signalling relay':
    'A public service used once, at the start, to help two browsers find each other. It only shepherds the encrypted handshake and never carries your files.',
  stun:
    'A public helper that tells your browser how it appears to the internet, so two devices behind home routers can find a direct path to each other.',
  'symmetric nat':
    'A stricter type of home/office router. If both devices are behind one, browsers sometimes can’t find a direct path without a relay — which we deliberately don’t run.',
  sas:
    'Short Authentication String — four words derived from your shared passphrase. If both devices show the same four words, no impostor slipped into the middle.',
  passphrase:
    'The shared secret for the pairing. It encrypts the handshake and derives the file key and the four verification words. It lives only in the link’s hash and never reaches any server.',
  'version clock':
    'Syncwell uses each file’s modified time as its version. The newer version wins; a file changed on both sides at the same instant is flagged and left untouched, never overwritten.',
  'two-way':
    'Both folders send and receive, so each device ends up with the newest copy of every file. Send-only pushes changes out; receive-only pulls them in.',
};

let tip: HTMLElement | null = null;

export function initGlossary(root: HTMLElement = document.body): void {
  root.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('.glossary-link') as HTMLElement | null;
    if (!target) {
      hide();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const term = target.dataset.term?.toLowerCase() ?? '';
    const def = GLOSSARY[term];
    if (!def) return;
    show(target, term, def);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
  });
  window.addEventListener('scroll', hide, true);
}

function show(anchor: HTMLElement, term: string, def: string): void {
  hide();
  tip = document.createElement('div');
  tip.className = 'glossary-tip';
  tip.innerHTML = `<div class="glossary-term"></div><div class="glossary-def"></div>`;
  (tip.querySelector('.glossary-term') as HTMLElement).textContent = term;
  (tip.querySelector('.glossary-def') as HTMLElement).textContent = def;
  document.body.appendChild(tip);
  const r = anchor.getBoundingClientRect();
  const tr = tip.getBoundingClientRect();
  let left = r.left;
  if (left + tr.width > window.innerWidth - 12) left = window.innerWidth - tr.width - 12;
  if (left < 12) left = 12;
  let top = r.bottom + 8;
  if (top + tr.height > window.innerHeight - 12) top = r.top - tr.height - 8;
  tip.style.left = `${left}px`;
  tip.style.top = `${Math.max(12, top)}px`;
}

function hide(): void {
  if (tip) {
    tip.remove();
    tip = null;
  }
}
